import { useState, useEffect } from 'react';
import { getAllSubscriptions } from '../../services/subscriptionService';
import { UnifiedSubscription, Platform, SubscriptionStatus } from '../../types/subscription';

const SubscriptionsTab: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<UnifiedSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [platformFilter, setPlatformFilter] = useState<Platform | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<SubscriptionStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        let data = await getAllSubscriptions();

        if (platformFilter !== 'all') {
          data = data.filter((sub) => sub.platform === platformFilter);
        }

        if (statusFilter !== 'all') {
          data = data.filter((sub) => sub.status === statusFilter);
        }

        if (searchTerm) {
          const search = searchTerm.toLowerCase();
          data = data.filter(
            (sub) =>
              sub.email.toLowerCase().includes(search) ||
              sub.userId.toLowerCase().includes(search) ||
              sub.planId.toLowerCase().includes(search)
          );
        }

        setSubscriptions(data);
      } catch (error) {
        console.error('Error loading subscriptions:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [platformFilter, statusFilter, searchTerm]);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading subscriptions...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Subscriptions
      </h1>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        flexWrap: 'wrap',
      }}>
        <input
          type="text"
          placeholder="Search by email, userId, or plan..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: '0.5rem 1rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            flex: 1,
            minWidth: '200px',
          }}
        />
        <select
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value as Platform | 'all')}
          style={{
            padding: '0.5rem 1rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        >
          <option value="all">All Platforms</option>
          <option value="stripe">Stripe</option>
          <option value="ios">iOS</option>
          <option value="android">Android</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as SubscriptionStatus | 'all')}
          style={{
            padding: '0.5rem 1rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="canceled">Canceled</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {/* Subscription List */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Platform</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Plan</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Amount</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Period End</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.slice(0, 100).map((sub, index) => (
              <tr key={`${sub.userId}-${sub.platform}-${index}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '1rem' }}>{sub.email || sub.userId}</td>
                <td style={{ padding: '1rem' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    backgroundColor: sub.platform === 'stripe' ? '#e3f2fd' : sub.platform === 'ios' ? '#f3e5f5' : '#fff3e0',
                    fontSize: '0.875rem',
                  }}>
                    {sub.platform.toUpperCase()}
                  </span>
                </td>
                <td style={{ padding: '1rem' }}>{sub.planId}</td>
                <td style={{ padding: '1rem' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    backgroundColor: sub.status === 'active' ? '#d1fae5' : sub.status === 'trialing' ? '#dbeafe' : '#fee2e2',
                    color: sub.status === 'active' ? '#065f46' : sub.status === 'trialing' ? '#1e40af' : '#991b1b',
                    fontSize: '0.875rem',
                  }}>
                    {sub.status}
                  </span>
                </td>
                <td style={{ padding: '1rem' }}>
                  ${sub.amount.toFixed(2)} {sub.currency.toUpperCase()}
                </td>
                <td style={{ padding: '1rem' }}>
                  {sub.currentPeriodEnd.toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {subscriptions.length === 0 && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
          No subscriptions found
        </div>
      )}
    </div>
  );
};

export default SubscriptionsTab;

